import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { InsuranceStatusErrorCode, InsuranceUsage } from '@typesApp/ClaimData'
import { ProductTypesEligibility } from '@typesApp/Product'
import { ProductQuote } from '@typesApp/ProductQuote'
import { ToggleContext } from '@typesApp/WidgetContextHolder'
import { isInsuranceUsageEnabled, isInsuranceUsageDisabled } from './insuranceMemberUtil'
import { isContactLensProductType, isFrameProductType } from './productTypeUtil'

type ToggleState = {
  toggleOn: boolean
  allowOn: boolean
  allowSelectProduct?: boolean
  allowDistributeBenefits?: boolean
}

type ToggleStateParams = {
  claimRecord?: ClaimRecord
  claimRecordDeleted?: boolean
}

type ToggleStateParamsForCart = ToggleStateParams & {
  variant?: ToggleContext['variant']
}

type ToggleMessageKeyParams = {
  initError: boolean | undefined
  productQuoteStatus:
    | {
        isError?: boolean
        isSuccess?: boolean
        productQuotes?: ProductQuote[]
      }
    | undefined
  claimRecordDeleted: boolean | undefined
  claimRecordStatus: ClaimRecordStatus | undefined
  insuranceStatusErrorCode: InsuranceStatusErrorCode | undefined
}

type ToggleEligibilityMessageKeyParams = {
  claimRecordStatus: ClaimRecordStatus | undefined
  claimRecordDeleted: boolean | undefined
  hasEligibleProduct: boolean | undefined
}

type ToggleMessageKeyForCartParams = {
  initError: boolean | undefined
  claimRecordDeleted: boolean | undefined
  useInsuranceStatus: InsuranceUsage | undefined
  insuranceStatusErrorCode: InsuranceStatusErrorCode | undefined
  isToggleClicked: boolean | undefined
}

const MESSAGE_KEY = {
  INIT_ERROR: 'init-error',
  INIT_ERROR_CART: 'init-error-cart',
  PRICING_DISCLAIMER: 'pricing-disclaimer',
  PRICING_DISCLAIMER_SALE_PRICE_BETTER: 'pricing-disclaimer-sale-price-better',
} as const

export const getInsuranceToggleState = (params: ToggleStateParams): ToggleState => {
  const { claimRecord, claimRecordDeleted } = params
  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)

  return {
    toggleOn,
    allowOn,
    allowSelectProduct: false,
    allowDistributeBenefits: false,
  }
}

const isToggleOn = (claimRecord: ClaimRecord | undefined, claimRecordDeleted?: boolean): boolean => {
  const insuranceUsage = claimRecord?.claimData?.useInsuranceStatus
  return !claimRecordDeleted && isInsuranceUsageEnabled(insuranceUsage)
}

export const getInsuranceToggleStateForCart = (params: ToggleStateParamsForCart): ToggleState => {
  const { claimRecord, claimRecordDeleted, variant } = params

  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)
  const showDistributeInsuranceBenefits = claimRecord?.claimData?.showDistributeInsuranceBenefits

  // hide select product for mini toggle
  const isMiniToggle = variant === 'mini'
  const allowSelectProduct = !isMiniToggle && Boolean(showDistributeInsuranceBenefits)

  return {
    toggleOn,
    allowOn,
    allowSelectProduct,
    allowDistributeBenefits: allowSelectProduct,
  }
}

const isProductQuoteSalePriceBetter = (productQuotes: ProductQuote[] | undefined): boolean => {
  if (!Array.isArray(productQuotes) || !productQuotes.length) return false

  // Note: consider only FRAME or CONTACTS in the quote for showing sale price better message
  const frameOrContacts = productQuotes.filter(
    item => isFrameProductType(item.productType) || isContactLensProductType(item.productType)
  )

  if (!frameOrContacts.length) return false

  return (
    frameOrContacts.filter(item => item.messageCode === 'SALE_PRICE_BETTER_THAN_INSURANCE_PRICE').length ===
    frameOrContacts.length
  )
}

const isProductQuoteBenefitsNotAvailable = (productQuotes: ProductQuote[] | undefined): boolean => {
  if (!Array.isArray(productQuotes) || !productQuotes.length) return false

  return (
    productQuotes.filter(item => item.memberBenefitStatus === 'BENEFITS_NOT_AVAILABLE').length === productQuotes.length
  )
}

export const getInsuranceToggleMessageKey = (params: ToggleMessageKeyParams): string | undefined => {
  const { claimRecordStatus, claimRecordDeleted, initError, productQuoteStatus, insuranceStatusErrorCode } = params
  if (claimRecordDeleted) return

  if (
    claimRecordStatus === ClaimRecordStatus.NO_BENEFITS_AVAILABLE ||
    insuranceStatusErrorCode === InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  ) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }

  if (productQuoteStatus?.isError) {
    return InsuranceStatusErrorCode.PRODUCT_QUOTE_FAILED
  }

  if (productQuoteStatus?.isSuccess) {
    if (isProductQuoteBenefitsNotAvailable(productQuoteStatus?.productQuotes)) {
      return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
    }

    const isSalePriceBetter = isProductQuoteSalePriceBetter(productQuoteStatus?.productQuotes)
    return isSalePriceBetter ? MESSAGE_KEY.PRICING_DISCLAIMER_SALE_PRICE_BETTER : MESSAGE_KEY.PRICING_DISCLAIMER
  }

  if (initError) {
    return MESSAGE_KEY.INIT_ERROR
  }
}

export const getInsuranceToggleEligibilityMessageKey = (
  params: ToggleEligibilityMessageKeyParams
): string | undefined => {
  const { claimRecordStatus, claimRecordDeleted, hasEligibleProduct } = params

  if (!claimRecordStatus || claimRecordStatus === ClaimRecordStatus.NONE || claimRecordDeleted) return

  // Note: for non cart, if product is not eligible we show 'benefits not available' message
  if (!hasEligibleProduct) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }
}

const IN_PROGRESS_ERROR_CODE = {
  SUFFIX: 'IN_PROGRESS',
  RELOAD_MESSAGE_KEY: 'APPLICATION_IN_PROGRESS',
  RETRY_MESSAGE_KEY: 'APPLICATION_IN_PROGRESS_RETRY',
} as const

const SUPPORTS_RETRY_STATUS_ERROR_CODES = [
  InsuranceStatusErrorCode.CART_QUOTE_FAILED,
  InsuranceStatusErrorCode.CLAIM_SUBMISSION_ERROR,
]

export const getInsuranceToggleMessageKeyForCart = (params: ToggleMessageKeyForCartParams): string | undefined => {
  const { isToggleClicked, useInsuranceStatus, insuranceStatusErrorCode, claimRecordDeleted, initError } = params
  if (claimRecordDeleted) return

  if (insuranceStatusErrorCode && insuranceStatusErrorCode !== InsuranceStatusErrorCode.NONE) {
    const inProgress = insuranceStatusErrorCode.endsWith(IN_PROGRESS_ERROR_CODE.SUFFIX)
    if (inProgress) {
      // toggle was not clicked and is enabled, show reload page message
      if (!isToggleClicked && isInsuranceUsageEnabled(useInsuranceStatus)) {
        return IN_PROGRESS_ERROR_CODE.RELOAD_MESSAGE_KEY
      }

      // fallback with retry message
      return IN_PROGRESS_ERROR_CODE.RETRY_MESSAGE_KEY
    }

    // codes that support different message for retry
    if (SUPPORTS_RETRY_STATUS_ERROR_CODES.includes(insuranceStatusErrorCode)) {
      return isInsuranceUsageEnabled(useInsuranceStatus)
        ? insuranceStatusErrorCode
        : `${insuranceStatusErrorCode}_RETRY`
    }

    return insuranceStatusErrorCode
  }

  if (initError) {
    return MESSAGE_KEY.INIT_ERROR_CART
  }
}

export const hasEligibleProductType = (productTypesEligibility: ProductTypesEligibility): boolean => {
  return productTypesEligibility?.productTypesEligibility?.some(item => item.benefitAvailability === 'SUPPORTED')
}
